import { apiServices } from "./ApiServices";

const urlBase64ToUint8Array = (base64String: string) => {
	const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
	const base64 = `${base64String}${padding}`
		.replace(/-/g, "+")
		.replace(/_/g, "/");

	const rawData = window.atob(base64);
	const outputArray = new Uint8Array(rawData.length);

	for (let index = 0; index < rawData.length; index += 1) {
		outputArray[index] = rawData.charCodeAt(index);
	}

	return outputArray;
};

const toUint8Array = (value: ArrayBuffer | ArrayBufferView) => {
	if (value instanceof ArrayBuffer) {
		return new Uint8Array(value);
	}

	return new Uint8Array(
		value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength)
	);
};

const hasMatchingApplicationServerKey = (
	subscription: PushSubscription,
	publicKey: string
) => {
	const applicationServerKey = subscription.options?.applicationServerKey;
	if (!applicationServerKey) {
		return false;
	}

	const currentKey = toUint8Array(applicationServerKey);
	const expectedKey = urlBase64ToUint8Array(publicKey);

	if (currentKey.length !== expectedKey.length) {
		return false;
	}

	return currentKey.every((byte, index) => byte === expectedKey[index]);
};

export const supportsBrowserPushSubscription = () => {
	return (
		typeof window !== "undefined" &&
		typeof navigator !== "undefined" &&
		"serviceWorker" in navigator &&
		"PushManager" in window
	);
};

export const getBrowserPushPublicKey = async () => {
	const response = await apiServices.get("/notification/push-subscription/public-key");
	return String(response?.data?.publicKey || "").trim();
};

export const registerBrowserPushSubscription = async (
	registration: ServiceWorkerRegistration,
	{ forceRefresh = false }: { forceRefresh?: boolean } = {}
) => {
	if (!supportsBrowserPushSubscription()) {
		return null;
	}

	const publicKey = await getBrowserPushPublicKey();
	if (!publicKey) {
		return null;
	}

	const existingSubscription = await registration.pushManager.getSubscription();
	if (existingSubscription) {
		const canReuseExistingSubscription =
			!forceRefresh &&
			hasMatchingApplicationServerKey(existingSubscription, publicKey);

		if (canReuseExistingSubscription) {
			return existingSubscription;
		}

		try {
			await existingSubscription.unsubscribe();
		} catch (error) {
			console.warn("Could not clear old browser push subscription:", error);
		}
	}

	return registration.pushManager.subscribe({
		userVisibleOnly: true,
		applicationServerKey: urlBase64ToUint8Array(publicKey),
	});
};

export const saveBrowserPushSubscription = async (subscription: PushSubscription) => {
	await apiServices.post("/notification/push-subscription", {
		subscription: subscription.toJSON(),
	});
};
